'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import './home.css';

export default function HomePage() {
  const router = useRouter();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // If already logged in, go straight to dashboard
    fetch('/api/auth/me', { credentials: 'include' })
      .then(res => {
        if (res.ok) {
          router.replace('/dashboard');
        } else {
          setChecking(false);
        }
      })
      .catch(() => setChecking(false));
  }, [router]);

  if (checking) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="home-page">
      {/* Modern Navbar */}
      <nav className="home-navbar">
        <div className="navbar-container">
          <Link href="/" className="navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src="/moniwatch.png" alt="Moniwatch Logo" style={{ height: '36px', width: 'auto' }} />
            <span className="navbar-text">Moniwatch</span>
          </Link>
          <div className="navbar-links">
            <Link href="#features" className="navbar-link">Features</Link>
            <Link href="#pricing" className="navbar-link">Pricing</Link>
            <Link href="#faq" className="navbar-link">FAQ</Link>
            <Link href="/login" className="navbar-link">Login</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-container">
          <div style={{ marginBottom: '20px' }}>
            <span style={{
              background: 'rgba(255, 255, 255, 0.2)', padding: '8px 16px',
              borderRadius: '20px', color: 'white', fontSize: '0.85rem', fontWeight: 600
            }}>
              ⭐ Trusted by 3,500+ Companies Worldwide
            </span>
          </div>
          <h1 className="hero-title">
            Employee Productivity Tracking<br />Made Simple
          </h1>
          <p className="hero-subtitle">
            Powerful employee monitoring software to track time, capture screenshots, and boost productivity for
            remote teams. Get real-time insights and detailed analytics.
          </p>
          <div className="hero-cta-group">
            {/* <Link href="/register" className="home-btn-primary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                <path d="M12 5v14M5 12h14" />
              </svg>
              Start Your Free Trial
            </Link> */}
            <Link href="#how-it-works" className="home-btn-secondary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                <circle cx="12" cy="12" r="10" />
                <polygon points="10 8 16 12 10 16 10 8" />
              </svg>
              Watch Demo
            </Link>
          </div>
          <p style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem', marginTop: '20px' }}>
            ✓ No credit card required ✓ 10-day free trial ✓ Cancel anytime
          </p>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="stats-section">
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-number">3,500+</div>
            <div className="stat-label">Active Companies</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">50K+</div>
            <div className="stat-label">Tracked Employees</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">99.9%</div>
            <div className="stat-label">Uptime SLA</div>
          </div>
          <div className="stat-item">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Support Available</div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section" id="how-it-works">
        <div className="features-container">
          <h2 className="section-title">How It Works</h2>
          <p className="section-subtitle">Get started in 3 simple steps</p>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="7.5 4.21 12 6.81 16.5 4.21" />
                  <polyline points="7.5 19.79 7.5 14.6 3 12" />
                  <polyline points="21 12 16.5 14.6 16.5 19.79" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                </svg>
              </div>
              <h3 className="step-title">Download &amp; Install</h3>
              <p className="step-description">Download our lightweight desktop agent and install it on your team&apos;s computers in minutes.</p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <line x1="20" y1="8" x2="20" y2="14" />
                  <line x1="23" y1="11" x2="17" y2="11" />
                </svg>
              </div>
              <h3 className="step-title">Add Your Team</h3>
              <p className="step-description">Invite team members via email and assign them to projects. Set up roles and permissions.</p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <h3 className="step-title">Track &amp; Analyze</h3>
              <p className="step-description">Monitor productivity in real-time, review screenshots, and generate detailed reports instantly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="home-features-section" id="features">
        <div className="features-container">
          <h2 className="section-title">Powerful Features for Modern Teams</h2>
          <p className="section-subtitle">Everything you need to manage remote teams effectively</p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <h3 className="feature-title">Screen Monitoring</h3>
              <p className="feature-description">Automatic screenshots at customizable intervals with secure cloud storage and easy review.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <h3 className="feature-title">Time Tracking</h3>
              <p className="feature-description">Accurate time tracking with automated timesheets and detailed activity reports.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </div>
              <h3 className="feature-title">Productivity Metrics</h3>
              <p className="feature-description">Real-time insights into team performance with comprehensive analytics.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
                </svg>
              </div>
              <h3 className="feature-title">Website Tracking</h3>
              <p className="feature-description">Monitor which websites and apps your team uses to identify patterns.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
              </div>
              <h3 className="feature-title">Attendance Management</h3>
              <p className="feature-description">Automated attendance tracking with clock-in/out features.</p>
            </div>

            <div className="feature-card">
              <div className="feature-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <h3 className="feature-title">Project Management</h3>
              <p className="feature-description">Assign tasks, track progress, and allocate resources seamlessly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="use-cases-section">
        <div className="features-container">
          <h2 className="section-title">Built for Every Industry</h2>
          <p className="section-subtitle">Trusted by teams across various sectors</p>

          <div className="use-cases-grid">
            <div className="use-case-card">
              <div className="use-case-icon">
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
                  <rect width="64" height="64" rx="12" fill="#E3F2FD" />
                  <rect x="14" y="20" width="36" height="24" rx="2" fill="#2196F3" />
                  <rect x="14" y="46" width="14" height="2" fill="#64B5F6" />
                  <rect x="36" y="46" width="14" height="2" fill="#64B5F6" />
                  <rect x="26" y="44" width="12" height="4" fill="#1976D2" />
                  <line x1="20" y1="26" x2="44" y2="26" stroke="#fff" strokeWidth="2" />
                  <line x1="20" y1="31" x2="38" y2="31" stroke="#fff" strokeWidth="2" />
                  <line x1="20" y1="36" x2="42" y2="36" stroke="#fff" strokeWidth="2" />
                </svg>
              </div>
              <h3 className="use-case-title">Software Development</h3>
              <p className="use-case-description">Track coding time, monitor development tools, and manage distributed dev teams.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
                  <rect width="64" height="64" rx="12" fill="#FCE4EC" />
                  <circle cx="32" cy="26" r="12" fill="#E91E63" />
                  <circle cx="20" cy="44" r="8" fill="#F06292" />
                  <circle cx="32" cy="48" r="6" fill="#EC407A" />
                  <circle cx="44" cy="44" r="8" fill="#F48FB1" />
                  <circle cx="38" cy="32" r="4" fill="#FF4081" />
                </svg>
              </div>
              <h3 className="use-case-title">Design Agencies</h3>
              <p className="use-case-description">Bill clients accurately and track time spent on creative projects.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
                  <rect width="64" height="64" rx="12" fill="#F3E5F5" />
                  <path d="M38 22C38 22 42 26 42 32C42 38 38 42 38 42" stroke="#9C27B0" strokeWidth="3" strokeLinecap="round" />
                  <path d="M42 18C42 18 48 24 48 32C48 40 42 46 42 46" stroke="#BA68C8" strokeWidth="2.5" strokeLinecap="round" />
                  <path d="M22 28H26C27 28 28 27 30 25L34 21C35 20 36 20 37 21C38 22 38 23 37 24L33 28C32 29 32 30 33 31L37 35C38 36 38 37 37 38C36 39 35 39 34 38L30 34C28 32 27 32 26 32H22V28Z" fill="#AB47BC" />
                  <rect x="18" y="26" width="8" height="8" rx="2" fill="#7B1FA2" />
                </svg>
              </div>
              <h3 className="use-case-title">Customer Support</h3>
              <p className="use-case-description">Monitor support team efficiency and response times.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
                  <rect width="64" height="64" rx="12" fill="#E8F5E9" />
                  <rect x="18" y="38" width="8" height="14" rx="2" fill="#4CAF50" />
                  <rect x="28" y="28" width="8" height="24" rx="2" fill="#66BB6A" />
                  <rect x="38" y="20" width="8" height="32" rx="2" fill="#81C784" />
                  <path d="M16 18L24 22L34 16L44 20L48 18" stroke="#2E7D32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="use-case-title">Marketing Teams</h3>
              <p className="use-case-description">Track campaign work hours and measure team productivity.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
                  <rect width="64" height="64" rx="12" fill="#FFF3E0" />
                  <rect x="20" y="26" width="24" height="18" rx="2" fill="#FF9800" />
                  <rect x="26" y="20" width="12" height="6" rx="1.5" fill="#F57C00" />
                  <circle cx="28" cy="34" r="2" fill="#FFE0B2" />
                  <circle cx="36" cy="34" r="2" fill="#FFE0B2" />
                  <rect x="28" y="38" width="8" height="2" rx="1" fill="#FFE0B2" />
                </svg>
              </div>
              <h3 className="use-case-title">BPO &amp; Outsourcing</h3>
              <p className="use-case-description">Ensure transparency and accountability for outsourced teams.</p>
            </div>

            <div className="use-case-card">
              <div className="use-case-icon">
                <svg width="56" height="56" viewBox="0 0 64 64" fill="none">
                  <rect width="64" height="64" rx="12" fill="#E1F5FE" />
                  <rect x="18" y="42" width="28" height="10" rx="1" fill="#0288D1" />
                  <rect x="22" y="32" width="20" height="10" rx="1" fill="#039BE5" />
                  <rect x="26" y="22" width="12" height="10" rx="1" fill="#03A9F4" />
                  <rect x="30" y="12" width="4" height="10" rx="1" fill="#29B6F6" />
                  <rect x="24" y="48" width="2" height="4" fill="#01579B" />
                  <rect x="28" y="48" width="2" height="4" fill="#01579B" />
                  <rect x="32" y="48" width="2" height="4" fill="#01579B" />
                  <rect x="36" y="48" width="2" height="4" fill="#01579B" />
                </svg>
              </div>
              <h3 className="use-case-title">Enterprise</h3>
              <p className="use-case-description">Scale monitoring across departments with enterprise features.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing-section" id="pricing">
        <div className="features-container">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle">Choose the plan that fits your team size</p>

          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-badge">Starter</div>
              <div className="pricing-price">
                <span className="price-currency">$</span>
                <span className="price-amount">6</span>
                <span className="price-period">/user/month</span>
              </div>
              <ul className="pricing-features">
                <li>✓ Screen monitoring</li>
                <li>✓ Time tracking</li>
                <li>✓ Activity reports</li>
                <li>✓ Up to 10 users</li>
                <li>✓ Email support</li>
              </ul>
              {/* <Link href="/register" className="pricing-cta">Get Started</Link> */}
            </div>

            <div className="pricing-card pricing-card-featured">
              <div className="pricing-badge pricing-badge-featured">Professional</div>
              <div className="pricing-price">
                <span className="price-currency">$</span>
                <span className="price-amount">9</span>
                <span className="price-period">/user/month</span>
              </div>
              <ul className="pricing-features">
                <li>✓ Everything in Starter</li>
                <li>✓ Advanced analytics</li>
                <li>✓ Project management</li>
                <li>✓ Unlimited users</li>
                <li>✓ Priority support</li>
                <li>✓ API access</li>
              </ul>
              {/* <Link href="/register" className="pricing-cta pricing-cta-featured">Get Started</Link> */}
            </div>

            <div className="pricing-card">
              <div className="pricing-badge">Enterprise</div>
              <div className="pricing-price">
                <span className="price-amount" style={{ fontSize: '2rem' }}>Custom</span>
              </div>
              <ul className="pricing-features">
                <li>✓ Everything in Professional</li>
                <li>✓ Custom integrations</li>
                <li>✓ Dedicated account manager</li>
                <li>✓ On-premise deployment</li>
                <li>✓ SLA guarantee</li>
                <li>✓ Custom contracts</li>
              </ul>
              <Link href="#" className="pricing-cta">Contact Sales</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="integrations-section">
        <div className="features-container">
          <h2 className="section-title">Seamless Integrations</h2>
          <p className="section-subtitle">Connect with your favorite tools</p>

          <div className="integrations-grid">
            {[
              { src: 'https://cdn.simpleicons.org/slack', name: 'Slack' },
              { src: 'https://cdn.simpleicons.org/jira/0052CC', name: 'Jira' },
              { src: 'https://cdn.simpleicons.org/asana/F06A6A', name: 'Asana' },
              { src: 'https://cdn.simpleicons.org/trello/0079BF', name: 'Trello' },
              { src: 'https://cdn.simpleicons.org/gmail', name: 'Gmail' },
              { src: 'https://cdn.simpleicons.org/zoom/2D8CFF', name: 'Zoom' },
            ].map((tool) => (
              <div key={tool.name} className="integration-card">
                <div className="integration-logo">
                  <img src={tool.src} alt={tool.name} width="56" height="56" />
                </div>
                <div className="integration-name">{tool.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="security-section">
        <div className="features-container">
          <h2 className="section-title">Enterprise-Grade Security</h2>
          <p className="section-subtitle">Your data is protected with industry-leading security</p>

          <div className="security-grid">
            <div className="security-feature">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <h4>256-bit Encryption</h4>
              <p>Bank-level encryption for all data</p>
            </div>
            <div className="security-feature">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              </svg>
              <h4>GDPR Compliant</h4>
              <p>Fully compliant with data regulations</p>
            </div>
            <div className="security-feature">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <h4>99.9% Uptime</h4>
              <p>Reliable service with SLA guarantee</p>
            </div>
            <div className="security-feature">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#667eea" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <h4>Role-Based Access</h4>
              <p>Granular permission controls</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="features-container">
          <h2 className="section-title">Trusted by Managers Worldwide</h2>
          <p className="section-subtitle">See what our customers have to say</p>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                &quot;Moniwatch transformed how we manage our distributed dev team. The screenshot feature gave
                us the confidence to hire globally.&quot;
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">SJ</div>
                <div>
                  <div className="author-name">Sarah Jenkins</div>
                  <div className="author-role">CTO, TechFlow</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                &quot;Simple, effective, and fair. My team actually appreciates the automated timesheets because they
                don&apos;t have to manually log hours.&quot;
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">DC</div>
                <div>
                  <div className="author-name">David Chen</div>
                  <div className="author-role">Founder, DesignCo</div>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-rating">⭐⭐⭐⭐⭐</div>
              <p className="testimonial-text">
                &quot;The best value for money in the market. We switched from a more expensive provider and haven&apos;t
                looked back.&quot;
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">MR</div>
                <div>
                  <div className="author-name">Michael Ross</div>
                  <div className="author-role">Ops Manager, LogisticsInc</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="faq-container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <p className="section-subtitle">Everything you need to know</p>

          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">How does the free trial work?</h3>
              <p className="faq-answer">Start your 10-day free trial with full access to all features. No credit card required. Cancel anytime before the trial ends.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Can employees see they&apos;re being monitored?</h3>
              <p className="faq-answer">Yes, we believe in transparency. Employees can see when tracking is active and can pause tracking during breaks.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">What platforms do you support?</h3>
              <p className="faq-answer">We support Windows, macOS, and Linux. Our web dashboard works on all modern browsers.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">How secure is my data?</h3>
              <p className="faq-answer">All data is encrypted with 256-bit SSL encryption and stored securely in SOC 2 certified data centers.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Can I customize screenshot intervals?</h3>
              <p className="faq-answer">Yes, you can set custom intervals from 1 to 30 minutes, or disable screenshots entirely.</p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Do you offer refunds?</h3>
              <p className="faq-answer">Yes, we offer a 30-day money-back guarantee. If you&apos;re not satisfied, we&apos;ll refund your payment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta-section">
        <div className="cta-container">
          <h2 className="cta-title">Ready to Boost Productivity?</h2>
          <p className="cta-description">
            Join thousands of companies using Moniwatch. Start your free trial today.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '30px' }}>
            {/* <Link href="/register" className="home-btn-primary">
              Start Free Trial
            </Link> */}
            <Link href="#" className="home-btn-secondary">
              Schedule a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <img src="/moniwatch.png" alt="Moniwatch Logo" style={{ height: '32px', width: 'auto' }} />
                <span style={{ fontWeight: 800, fontSize: '1.25rem', color: 'white' }}>Moniwatch</span>
              </div>
              <p className="footer-description">
                The #1 Employee Monitoring and Time Tracking software for remote teams. Secure, transparent, and easy to use.
              </p>
              <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
                <Link href="#" style={{ color: '#94a3b8', transition: 'color 0.2s' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </Link>
                <Link href="#" style={{ color: '#94a3b8', transition: 'color 0.2s' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                </Link>
                <Link href="#" style={{ color: '#94a3b8', transition: 'color 0.2s' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </Link>
              </div>
            </div>

            <div className="footer-column">
              <h4>Product</h4>
              <div className="footer-links">
                <Link href="#features" className="footer-link">Features</Link>
                <Link href="#pricing" className="footer-link">Pricing</Link>
                <Link href="#" className="footer-link">Download</Link>
                <Link href="#" className="footer-link">Integrations</Link>
                <Link href="#how-it-works" className="footer-link">How It Works</Link>
              </div>
            </div>

            <div className="footer-column">
              <h4>Resources</h4>
              <div className="footer-links">
                <Link href="#" className="footer-link">Blog</Link>
                <Link href="#" className="footer-link">Help Center</Link>
                <Link href="#faq" className="footer-link">FAQ</Link>
                <Link href="#" className="footer-link">ROI Calculator</Link>
                <Link href="#" className="footer-link">API Docs</Link>
              </div>
            </div>

            <div className="footer-column">
              <h4>Legal</h4>
              <div className="footer-links">
                <Link href="#" className="footer-link">Privacy Policy</Link>
                <Link href="#" className="footer-link">Terms of Service</Link>
                <Link href="#" className="footer-link">GDPR</Link>
                <Link href="#" className="footer-link">Security</Link>
                <Link href="#" className="footer-link">Compliance</Link>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            © 2026 Moniwatch Enterprise. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
